import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import TipTapEditor from '../../../shared/rich-text/TipTapEditor';

const getDetailTabTitle = (tab) => {
  if (!tab) return '';
  const title = (tab.TabTitle || '').trim();
  return title || tab.TypeDefaultTitle || tab.TabKey || '';
};

function isHtmlBlockTab(tab) {
  return tab?.ContentType === 'html_block';
}

function ProductDetailTabContentTab({
  productId,
  detailTabs = [],
  readOnly = false,
  onRefresh,
}) {
  const [alert, setAlert] = useState(null);
  const [selectedTabKey, setSelectedTabKey] = useState('');
  const [draftHtml, setDraftHtml] = useState('');
  const [saving, setSaving] = useState(false);

  const htmlTabs = useMemo(
    () => (detailTabs || []).filter((tab) => isHtmlBlockTab(tab)),
    [detailTabs],
  );

  const selectedTab = useMemo(
    () => htmlTabs.find((t) => t.TabKey === selectedTabKey) || null,
    [htmlTabs, selectedTabKey],
  );

  const handleSelectTab = (tabKey) => {
    const tab = htmlTabs.find((t) => t.TabKey === tabKey);
    setSelectedTabKey(tabKey);
    setDraftHtml(tab?.TabHtmlContent || '');
  };

  const saveContent = async () => {
    if (!productId || !selectedTabKey) {
      CreateAlert('Select a tab to save content', 'Warning', setAlert);
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/detail-tab/html/save`, {
        ProductId: productId,
        TabKey: selectedTabKey,
        TabHtmlContent: draftHtml || null,
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Tab content saved', 'Success', setAlert);
        if (onRefresh) onRefresh();
      } else {
        CreateAlert(result?.message || 'Failed to save tab content', 'Error', setAlert);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to save tab content', 'Error', setAlert);
    } finally {
      setSaving(false);
    }
  };

  if (!productId) {
    return <div className="alert alert-info mb-0">Save the product details first to edit tab content.</div>;
  }

  if (htmlTabs.length === 0) {
    return (
      <div className="alert alert-warning mb-0">
        No HTML tabs are configured. Enable tab types with content type &quot;HTML block&quot; and save Detail Page Tabs first.
      </div>
    );
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <p className="text-muted small mb-3">
        Choose a detail tab, edit content in the editor, then save. Enable tabs and set titles under Detail Page Tabs.
      </p>
      <div className="row g-2 mb-2">
        <div className="col-lg-6">
          <label className="form-label small mb-1">Detail tab</label>
          <select
            className="form-select"
            value={selectedTabKey}
            onChange={(e) => handleSelectTab(e.target.value)}
          >
            <option value="">Select tab</option>
            {htmlTabs.map((tab) => (
              <option key={tab.TabKey} value={tab.TabKey}>
                {getDetailTabTitle(tab)} ({tab.TabKey})
              </option>
            ))}
          </select>
        </div>
      </div>
      {selectedTab ? (
        <>
          <TipTapEditor
            key={selectedTabKey}
            value={draftHtml}
            onChange={setDraftHtml}
            readOnly={readOnly}
            productId={productId}
            placeholder="Write product tab content…"
            className="mb-2"
          />
          {!readOnly ? (
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-submit" onClick={saveContent} disabled={saving}>
                {saving ? 'Saving…' : 'Save content'}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-muted mb-0">Select a tab to edit its content.</p>
      )}
    </>
  );
}

export default ProductDetailTabContentTab;
